import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FiPenTool, FiCpu } from 'react-icons/fi';

import Button from '../../component/button/button';
import Header from '../../component/header/header';
import HierarchyPathDisplay from '../../components/HierarchyPathDisplay';
import ConfirmModal from '../Modal/ConfirmModal';
import {
  getTemplateById,
  Template,
  TopicWithQuestions,
} from 'src/api/template';
import {
  generateQuestionsFromTemplate,
  getGenerationJob,
  isTerminalGenerationStatus,
  listGenerationJobs,
  QuestionGenerationJob,
} from 'src/api/questionGeneration';
import { hierarchyTopicKey } from '../../utils/hierarchyTopic';
import {
  errorMessageShow,
  LoaderComponent,
  successMessage,
} from '../../utils/Helper';

type TemplateProps = {
  templateId?: string;
};

const countTemplateQuestions = (template: Template | null) =>
  template?.topic_groups?.reduce(
    (total, group) =>
      total +
      (group.topics || []).reduce(
        (topicTotal, topic) => topicTotal + (topic.questions?.length || 0),
        0
      ),
    0
  ) || 0;

export default function SingleTemplate({ templateId }: TemplateProps) {
  const location = useLocation();
  const { templateId: id } = useParams();
  const [data, setData] = useState<Template | null>(null);
  const [loading, setLoading] = useState<boolean>(
    !!id && !location.pathname.includes('/templates/create')
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [job, setJob] = useState<QuestionGenerationJob | null>(null);

  const navigate = useNavigate();
  const questionCount = useMemo(() => countTemplateQuestions(data), [data]);

  useEffect(() => {
    if (id) {
      setLoading(true);

      getTemplateById(id)
        .then(res => {
          setData(res?.data);
        })
        .catch(err => {
          console.error('Error fetching template:', err);
        })
        .finally(() => {
          setLoading(false);
        });

      listGenerationJobs(id)
        .then(response => {
          const jobs = response.data?.data || [];
          if (jobs.length) {
            setJob(current => current ?? jobs[0]);
          }
        })
        .catch(() => undefined);
    }
  }, [id, location.pathname]);

  const refreshJob = useCallback(() => {
    if (!job?.id || isTerminalGenerationStatus(job.status)) {
      return;
    }
    getGenerationJob(job.id)
      .then(response => {
        setJob(response.data.data);
      })
      .catch(() => undefined);
  }, [job?.id, job?.status]);

  useEffect(() => {
    if (!job?.id || isTerminalGenerationStatus(job.status)) {
      return undefined;
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshJob();
      }
    };
    const onFocus = () => refreshJob();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [job?.id, job?.status, refreshJob]);

  const startGeneration = async () => {
    if (!id) return;
    setStarting(true);
    try {
      const response = await generateQuestionsFromTemplate(id);
      setJob(response.data.data);
      setConfirmOpen(false);
      successMessage('Question generation started.');
    } catch (error: any) {
      errorMessageShow(
        error?.response?.data?.message ||
          error?.response?.data?.detail ||
          'Unable to start question generation.'
      );
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="content templates flex gap-[16px]">
      <Header
        pageTitle={data?.name || ''}
        buttonText="Use"
        isActive={data?.status === 'published'}
        para={
          data?.status === 'draft' ? (
            <p className="text-sm text-[#667085]">
              Publish this template before creating an exam.
            </p>
          ) : undefined
        }
        onClick={() => {
          if (data?.status !== 'published') return;
          navigate(`/templates/template-use/${id}`);
        }}
        extraButton={
          <>
            <Button
              style={{ marginLeft: '5px' }}
              type="secondary"
              buttonType="button"
              icon={<FiCpu />}
              text="Generate questions"
              isLoading={starting}
              onClick={() => setConfirmOpen(true)}
            />
            <Button
              style={{ marginLeft: '5px' }}
              type="secondary"
              buttonType="button"
              onClick={() => {
                navigate(`/templates/template/${id}/edit`);
              }}
              icon={<FiPenTool />}
              text="Edit"
            />
          </>
        }
      />

      {job && (
        <GenerationJobBanner
          job={job}
          onReview={() =>
            navigate(
              `/questions?generation_job_id=${job.id}&verification_status=unverified`
            )
          }
        />
      )}

      <div className="box-shadow px-[40px]  py-[70px] rounded-[4px] sm:w-full lg:w-[80%] flex flex-col gap-[24px] ">
        {loading ? (
          <LoaderComponent label="Loading template…" />
        ) : data?.topic_groups?.length ? (
          data.topic_groups.map((item, index) => {
            return (
              <div key={index} className="flex flex-col gap-[24px]">
                <h2 className="text-black text-base font-bold leading-6">
                  {item?.name}
                </h2>
                <EachTopic topics={item.topics} />
              </div>
            );
          })
        ) : (
          <p className="text-gray-500 text-sm">No template content found.</p>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmOpen}
        title="Generate questions"
        message={`Generate ${questionCount} question${
          questionCount === 1 ? '' : 's'
        } from this template? They will be added as drafts for you to verify.`}
        confirmLabel="Generate questions"
        cancelLabel="Cancel"
        isLoading={starting}
        onConfirm={startGeneration}
        onCancel={() => {
          if (!starting) setConfirmOpen(false);
        }}
      />
    </div>
  );
}

const GenerationJobBanner = ({
  job,
  onReview,
}: {
  job: QuestionGenerationJob;
  onReview: () => void;
}) => {
  const terminal = isTerminalGenerationStatus(job.status);
  const failed = job.status === 'failed';
  return (
    <div className="w-full lg:w-[80%] rounded-[4px] border border-[#E4E7EC] bg-white px-5 py-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="text-sm font-semibold text-[#1D2939] capitalize">
          Generation {job.status.replace(/_/g, ' ')}
        </p>
        <p className="text-sm text-[#475467]">
          {job.imported_count} of {job.requested_count} questions ready for
          review
          {job.failed_count ? ` · ${job.failed_count} slot(s) failed` : ''}
        </p>
      </div>
      {terminal && !failed && job.imported_count > 0 && (
        <Button
          type="secondary"
          text="Review questions"
          buttonType="button"
          onClick={onReview}
        />
      )}
    </div>
  );
};

const EachTopic = ({ topics }: { topics: TopicWithQuestions[] }) => {
  return (
    <div className="flex flex-col gap-[24px]">
      {topics.map(item => {
        return (
          <div
            key={hierarchyTopicKey(item)}
            className="w-full flex flex-col gap-[8px]  items-start p-1  self-stretch rounded-md border border-[#E4E7EC] bg-white"
          >
            <div className="px-4 pt-2">
              <HierarchyPathDisplay topic={item} variant="rows" />
            </div>

            <div className="questions w-full flex flex-col gap-[2px]">
              {item.questions.map((question, index) => {
                return (
                  <div
                    key={index}
                    className="flex  w-full items-center  self-stretch max-h-[320px] rounded-sm bg-[#E4E7EC] p-1 px-3"
                  >
                    <h4 className="flex  w-full gap-[12px] text-[#1D2939] text-sm font-semibold leading-5 ">
                      <span className="w-6">{index}</span>
                      <span className="flex-1 capitalize">
                        {question.complexity}
                      </span>
                      <span>{question.number}</span>
                    </h4>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
